import { plural, text } from './report-values';

import type { FinalRecord } from './final-report';
import type {
    CellStatus,
    LabelledValue,
    MappingRow,
    ModuleReportView,
    Observation,
} from './module-report-types';
import type { ReportIssue } from './report-issues';
import type { ClientSheet } from './report-sections';

/**
 * Company Information's page in the final report (INI-0468). It reads the
 * columns the backend's `CompanyInformationMigrator` declares — the RAW
 * Salesforce values (`legalNameSf`, `taxIdSf`, …), `websiteV2`, the comma list
 * `notMigratedFields`, `primaryCount`, `v2State` — and the warning codes
 * `migrate()` raises. The record sends no other Veevart 2.0 value, so the V2
 * side names the profile field each value maps to, and only the website shows
 * what V2 stores.
 *
 * Operators see every raw Salesforce value, refused ones included, in the
 * Analyze and Launch steps, the final report and every download: Product needs
 * them to correct the source. They are read from the `*Sf` columns only. A
 * `COMPANY_INFORMATION_FIELD_NOT_MIGRATED` warning names the field and the
 * profile's reason, never the value, so nothing here parses a value out of a
 * message.
 */

const FIELD_NOT_MIGRATED = 'COMPANY_INFORMATION_FIELD_NOT_MIGRATED';
const MULTIPLE_PRIMARIES = 'COMPANY_INFORMATION_MULTIPLE_PRIMARY_COMPANIES';
const NOTHING_TO_MIGRATE = 'COMPANY_INFORMATION_NOTHING_TO_MIGRATE';

const TOPICS: Readonly<Record<string, string>> = {
    [FIELD_NOT_MIGRATED]: 'refused profile fields',
    [MULTIPLE_PRIMARIES]: 'duplicate primary records',
    [NOTHING_TO_MIGRATE]: 'empty company information',
};

interface ProfileField {
    /** The Veevart 2.0 profile field, as the backend names it (`CompanyProfileField`). */
    readonly path: string;
    readonly label: string;
    /** The `Auctifera__Company_Information__c` field the runner reads. */
    readonly salesforce: string;
    /** The record column that carries the raw Salesforce value. */
    readonly column: string;
}

/** In the backend's own order (`mapping/plan-company-profile.ts`, `FIELD_MAP`). */
export const PROFILE_FIELDS: readonly ProfileField[] = [
    { path: 'legalName', label: 'Legal name', salesforce: 'Name', column: 'legalNameSf' },
    { path: 'taxId', label: 'Tax ID', salesforce: 'Auctifera__Tax_ID__c', column: 'taxIdSf' },
    {
        path: 'postalAddress.line1',
        label: 'Address line 1',
        salesforce: 'Auctifera__Address__c',
        column: 'addressSf',
    },
    {
        path: 'postalAddress.line2',
        label: 'Address line 2',
        salesforce: 'Auctifera__Address_Additional_Details__c',
        column: 'addressDetailsSf',
    },
    {
        path: 'postalAddress.city',
        label: 'City',
        salesforce: 'Auctifera__City__c',
        column: 'citySf',
    },
    {
        path: 'postalAddress.state',
        label: 'State',
        salesforce: 'Auctifera__State__c',
        column: 'stateSf',
    },
    {
        path: 'postalAddress.postalCode',
        label: 'Postal code',
        salesforce: 'Auctifera__Postal_Code__c',
        column: 'postalCodeSf',
    },
    {
        path: 'postalAddress.country',
        label: 'Country',
        salesforce: 'Auctifera__Country__c',
        column: 'countrySf',
    },
    {
        path: 'supportEmail',
        label: 'Support email',
        salesforce: 'Auctifera__eMail__c',
        column: 'emailSf',
    },
    {
        path: 'supportPhone',
        label: 'Support phone',
        salesforce: 'Auctifera__Company_Telephone__c',
        column: 'phoneSf',
    },
    {
        path: 'websiteUrl',
        label: 'Website',
        salesforce: 'Auctifera__Website__c',
        column: 'websiteSf',
    },
];

/** `<field> was not migrated: …`: the field a refusal names. */
const FIELD_LEAD = /^(\S+) was not migrated:/;

/** Every parenthesised part of a refusal; the profile's reason is one of them. */
const PARENTHESISED = /\(([^()]*)\)/g;

function fieldOf(path: string | undefined): ProfileField | null {
    return PROFILE_FIELDS.find((field) => field.path === path) ?? null;
}

/**
 * The profile's own reason (`supportEmail must be an email address.`), and only
 * that shape. An address part names its leaf key (`line1 must be …`): it is
 * written back under the full path.
 */
function keptReason(reason: string, field: ProfileField): string | null {
    const trimmed = reason.trim();
    const leaf = field.path.split('.').pop() ?? field.path;
    const lead = [field.path, leaf].find((name) => trimmed.startsWith(`${name} must `));
    if (lead === undefined) return null;
    return `${field.path}${trimmed.slice(lead.length)}`.replace(/\.$/, '');
}

/**
 * The reason a `FIELD_NOT_MIGRATED` warning gives for `field`, or `null`. Only
 * the field and the reason are read from the message: the value comes from the
 * record's `*Sf` column (the backend no longer quotes it in the message). The
 * LAST reason-shaped part wins, so an older message that still quoted a value
 * holding parentheses reads the same.
 */
function refusalReason(message: string, field: ProfileField): string | null {
    if (FIELD_LEAD.exec(message)?.[1] !== field.path) return null;
    const reasons = [...message.matchAll(PARENTHESISED)]
        .map((match) => keptReason(match[1] ?? '', field))
        .filter((reason): reason is string => reason !== null);
    return reasons.at(-1) ?? null;
}

function warningsOf(record: FinalRecord, code: string): ReportIssue[] {
    return (record.tracking?.warnings ?? []).filter((warning) => warning.code === code);
}

/** The profile fields whose Salesforce value was refused: the column's list and the warnings. */
export function refusedFields(record: FinalRecord): ReadonlySet<string> {
    const listed = (text(record.columns.notMigratedFields) ?? '').split(',');
    const warned = warningsOf(record, FIELD_NOT_MIGRATED).map(
        (warning) => FIELD_LEAD.exec(warning.message)?.[1],
    );
    return new Set(
        [...listed.map((path) => path.trim()), ...warned]
            .map((path) => fieldOf(path)?.path)
            .filter((path): path is string => path !== undefined),
    );
}

/** Where the record got to: what the run wrote, what it still will, or neither. */
type RecordState = 'migrated' | 'planned' | 'stopped';

function recordState(record: FinalRecord): RecordState {
    const { tracking } = record;
    if (record.notMigrated !== null || warningsOf(record, NOTHING_TO_MIGRATE).length > 0) {
        return 'stopped';
    }
    if (tracking?.status === 'Migrated') return 'migrated';
    if (tracking !== null && (tracking.failure !== null || tracking.status === 'Error')) {
        return 'stopped';
    }
    return 'planned';
}

/** Salesforce had `www.museum.org` and V2 stores `https://www.museum.org`. */
export function websiteNormalized(record: FinalRecord): boolean {
    const salesforce = text(record.columns.websiteSf);
    const veevart = text(record.columns.websiteV2);
    return salesforce !== null && veevart === `https://${salesforce}`;
}

const NOT_MIGRATED: CellStatus = { label: 'not migrated', tone: 'warning' };
const DASH: CellStatus = { label: '—', tone: 'neutral' };

function fieldStatus(record: FinalRecord, field: ProfileField, refused: boolean): CellStatus {
    if (refused) return NOT_MIGRATED;
    if (text(record.columns[field.column]) === null)
        return { label: 'none in SF', tone: 'neutral' };
    const state = recordState(record);
    if (state === 'stopped') return DASH;
    if (state === 'planned') return { label: 'planned', tone: 'info' };
    return field.path === 'websiteUrl' && websiteNormalized(record)
        ? { label: 'normalized', tone: 'success' }
        : { label: 'migrated', tone: 'success' };
}

/** The website V2 stores (or will), with the scheme the migration added. */
function websiteNote(record: FinalRecord): string | null {
    const veevart = text(record.columns.websiteV2);
    const state = recordState(record);
    if (veevart === null || state === 'stopped') return null;
    const added = websiteNormalized(record) ? ' · https:// added' : '';
    return state === 'migrated' ? `${veevart}${added}` : `Planned: ${veevart}${added}`;
}

export function companyInformationMappingRows(record: FinalRecord): MappingRow[] {
    const refused = refusedFields(record);
    return PROFILE_FIELDS.map((field) => {
        const isRefused = refused.has(field.path);
        return {
            attribute: field.label,
            salesforce: {
                text: field.salesforce,
                mono: true,
                // The raw value, refused or not: it is what the operator corrects.
                note: text(record.columns[field.column]),
            },
            veevart: {
                text: field.path,
                mono: true,
                note: field.path === 'websiteUrl' && !isRefused ? websiteNote(record) : null,
            },
            status: fieldStatus(record, field, isRefused),
        };
    });
}

function primaryCount(record: FinalRecord): number | null {
    const count = record.columns.primaryCount;
    return typeof count === 'number' ? count : null;
}

/** Which of several primaries the record is, as far as the run got. */
const OLDEST: Readonly<Record<RecordState, string>> = {
    migrated: ' · oldest migrated',
    planned: ' · oldest used',
    stopped: '',
};

export function companyInformationFacts(record: FinalRecord): LabelledValue[] {
    const primaries = primaryCount(record);
    const refused = refusedFields(record).size;
    return [
        primaries === null
            ? null
            : {
                  label: 'Primary records',
                  value: `${String(primaries)}${primaries > 1 ? OLDEST[recordState(record)] : ''}`,
              },
        refused === 0 ? null : { label: 'Fields not migrated', value: String(refused) },
    ].filter((fact): fact is LabelledValue => fact !== null);
}

/** Each distinct reason the profile gave for a field. */
function refusalReasons(records: readonly FinalRecord[], field: ProfileField): string[] {
    const reasons = records.flatMap((record) =>
        warningsOf(record, FIELD_NOT_MIGRATED)
            .map((warning) => refusalReason(warning.message, field))
            .filter((reason): reason is string => reason !== null),
    );
    return [...new Set(reasons)];
}

/** How many refused values an observation quotes, and how long each may be. */
const EXAMPLE_LIMIT = 3;
const EXAMPLE_LENGTH = 60;

/** A few of the refused raw values, from the `*Sf` column, distinct and shortened. */
function refusedExamples(records: readonly FinalRecord[], field: ProfileField): string | null {
    const values = [
        ...new Set(
            records
                .map((record) => text(record.columns[field.column]))
                .filter((value): value is string => value !== null),
        ),
    ];
    if (values.length === 0) return null;
    const shown = values
        .slice(0, EXAMPLE_LIMIT)
        .map((value) =>
            value.length > EXAMPLE_LENGTH
                ? `"${value.slice(0, EXAMPLE_LENGTH - 1)}…"`
                : `"${value}"`,
        )
        .join(', ');
    const more = values.length - EXAMPLE_LIMIT;
    return more > 0 ? `${shown} and ${String(more)} more` : shown;
}

/**
 * Across all clients, one line per profile field that was refused anywhere —
 * how many clients, the profile's reasons and a few of the refused Salesforce
 * values — then duplicate primaries, normalized websites and empty profiles.
 */
export function companyInformationObservations(sheets: readonly ClientSheet[]): Observation[] {
    const records = sheets
        .map((sheet) => sheet.record)
        .filter((record): record is FinalRecord => record !== null);

    const byField = PROFILE_FIELDS.map((field): Observation | null => {
        const hits = records.filter((record) => refusedFields(record).has(field.path));
        if (hits.length === 0) return null;
        const reasons = refusalReasons(hits, field);
        const examples = refusedExamples(hits, field);
        return {
            label: field.label,
            code: FIELD_NOT_MIGRATED,
            text:
                `Not migrated for ${plural(hits.length, 'client')}: Veevart 2.0 refused the ` +
                `Salesforce value${reasons.length === 0 ? '' : ` (${reasons.join('; ')})`}. ` +
                `${examples === null ? '' : `Salesforce had ${examples}. `}` +
                'Correct them in Salesforce and run the migration again, or fix them in ' +
                'Veevart 2.0 (no re-run needed).',
        };
    });

    const duplicated = records.filter((record) => (primaryCount(record) ?? 0) > 1).length;
    // Only what the run wrote: a planned, failed or not migrated record added nothing.
    const normalized = records.filter(
        (record) => recordState(record) === 'migrated' && websiteNormalized(record),
    ).length;
    const empty = records.filter(
        (record) => warningsOf(record, NOTHING_TO_MIGRATE).length > 0,
    ).length;

    return [
        ...byField,
        duplicated === 0
            ? null
            : {
                  label: 'Primary records',
                  code: MULTIPLE_PRIMARIES,
                  text:
                      `${plural(duplicated, 'client has', 'clients have')} more than one Company ` +
                      'Information record marked as primary; the migration uses the oldest one. ' +
                      'Leave only one marked as Primary Company in Salesforce.',
              },
        normalized === 0
            ? null
            : {
                  label: 'Website',
                  code: null,
                  text:
                      `https:// was added to the Salesforce website of ${plural(normalized, 'client')}: ` +
                      'Veevart 2.0 stores an absolute URL.',
              },
        empty === 0
            ? null
            : {
                  label: 'Nothing to migrate',
                  code: NOTHING_TO_MIGRATE,
                  text:
                      `Nothing was migrated for ${plural(empty, 'client')}: no Company Information ` +
                      'value is usable in Veevart 2.0.',
              },
    ].filter((observation): observation is Observation => observation !== null);
}

export const COMPANY_INFORMATION_REPORT: ModuleReportView = {
    recordNoun: 'organization profile',
    table: (record) => ({ kind: 'mapping', rows: companyInformationMappingRows(record) }),
    preview: () => null,
    recordChip: (record) => {
        const state = text(record.columns.v2State);
        return state === null ? null : `Veevart 2.0 profile: ${state}`;
    },
    facts: companyInformationFacts,
    observations: companyInformationObservations,
    explainedWarningCodes: new Set([FIELD_NOT_MIGRATED, MULTIPLE_PRIMARIES, NOTHING_TO_MIGRATE]),
    warningTopic: (code) => TOPICS[code] ?? null,
    launchColumns: true,
};
